import React, { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import { ChatRequest, ChatResponse, ChatContext, MemoirChatStats, UserProfile, UpgradePromptData, SubscriptionTier } from '../types/api';
import { apiClient } from '../services/api';
import UpgradePrompt from './UpgradePrompt';
import './ChatInterface.css';

interface ChatInterfaceProps {
  memoirId: string;
  userProfile: UserProfile;
  isVisible: boolean;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
  contextUsed?: ChatContext[];
  error?: string;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  memoirId,
  userProfile,
  isVisible
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatStats, setChatStats] = useState<MemoirChatStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReadyMessage, setShowReadyMessage] = useState(true);
  const [readyMessageAnimating, setReadyMessageAnimating] = useState(false);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Check if user has AI access (all subscription tiers have access, just different limits)
  const hasAIAccess = true;

  // Check if user has reached their AI usage limit
  const hasReachedLimit = userProfile.ai_usage_count >= userProfile.ai_usage_limit;

  // Load chat stats when component mounts or memoir changes
  useEffect(() => {
    if (memoirId) {
      loadChatStats();
    }
  }, [memoirId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat becomes visible
  useEffect(() => {
    if (isVisible && !hasReachedLimit && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible, hasReachedLimit]);

  // Auto-hide "Ready to Chat" message after 3 seconds with slide-up animation
  useEffect(() => {
    if (chatStats?.chat_ready && showReadyMessage && !readyMessageAnimating) {
      const animationTimer = setTimeout(() => {
        setReadyMessageAnimating(true);

        // Hide completely after animation finishes (300ms animation duration)
        const hideTimer = setTimeout(() => {
          setShowReadyMessage(false);
        }, 300);

        return () => clearTimeout(hideTimer);
      }, 3000);

      return () => clearTimeout(animationTimer);
    }
  }, [chatStats?.chat_ready, showReadyMessage, readyMessageAnimating]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatStats = async () => {
    try {
      setStatsLoading(true);
      const stats = await apiClient.getMemoirChatStats(memoirId);
      setChatStats(stats);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load chat stats:', err);
      setError(err.response?.data?.detail || 'Failed to load chat information');
    } finally {
      setStatsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Check if user has reached their AI usage limit - show popup instead of sending
    if (hasReachedLimit) {
      setShowUpgradePopup(true);
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Create placeholder for streaming response
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      type: 'assistant',
      content: '',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Use streaming endpoint
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/ai/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage.content,
          memoir_id: memoirId
        })
      });

      if (!response.ok) {
        // Handle error responses
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to send message';

        if (response.status === 429) {
          errorMessage = errorData.detail?.message || 'AI usage limit exceeded';
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : errorData.detail.message || 'An error occurred';
        }

        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Stream not available');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.token) {
                // Update the streaming message with new token
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: msg.content + data.token }
                    : msg
                ));
              }

              if (data.done) {
                // Stream completed - update timestamp
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, timestamp: new Date().toISOString() }
                    : msg
                ));
              }

              if (data.error) {
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }

      setError(null);

    } catch (err: any) {
      console.error('Chat error:', err);

      const errorContent = err.message || 'I apologize, but I encountered an error processing your message. Please try again.';

      // Update the assistant message with error
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, content: errorContent, error: errorContent }
          : msg
      ));
      setError(errorContent);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Enhanced markdown-style formatter
  const formatMessageText = (text: string) => {
    return text.split('\n').map((line, index) => {
      // Headers (### Header)
      if (line.startsWith('###')) {
        return <h3 key={index} className="chat-h3">{line.replace(/^###\s*/, '')}</h3>;
      }
      if (line.startsWith('##')) {
        return <h2 key={index} className="chat-h2">{line.replace(/^##\s*/, '')}</h2>;
      }
      if (line.startsWith('#')) {
        return <h1 key={index} className="chat-h1">{line.replace(/^#\s*/, '')}</h1>;
      }

      // Bold text (**text**)
      if (line.includes('**')) {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
          <p key={index} className="chat-paragraph">
            {parts.map((part, i) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
              }
              return formatInlineMarkdown(part, i);
            })}
          </p>
        );
      }

      // Bullet points (- item or * item)
      if (line.match(/^[\-\*]\s+/)) {
        return (
          <li key={index} className="chat-list-item">
            {formatInlineMarkdown(line.replace(/^[\-\*]\s+/, ''))}
          </li>
        );
      }

      // Empty lines
      if (!line.trim()) {
        return <br key={index} />;
      }

      // Regular paragraphs
      return (
        <p key={index} className="chat-paragraph">
          {formatInlineMarkdown(line)}
        </p>
      );
    });
  };

  // Helper to format inline markdown (italic, code, etc.)
  const formatInlineMarkdown = (text: string, keyPrefix: number = 0): React.ReactNode => {
    // Handle italic text (*text* or _text_)
    const italicRegex = /(\*[^*]+\*|_[^_]+_)/g;
    const italicParts = text.split(italicRegex);

    return italicParts.map((part, i) => {
      if ((part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) ||
        (part.startsWith('_') && part.endsWith('_') && !part.startsWith('__'))) {
        return <em key={`${keyPrefix}-${i}`}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  // Upgrade popup data for when user tries to use chat with no credits
  const upgradeData: UpgradePromptData = {
    type: 'ai_usage_limit',
    current_plan: userProfile.subscription_tier,
    suggested_plan: userProfile.subscription_tier?.toLowerCase() === 'premium' ? 'ai_credits' as SubscriptionTier : 'premium' as SubscriptionTier,
    message: `You've reached your AI usage limit of ${userProfile.ai_usage_limit} requests. Upgrade to get more AI requests and continue using AI features.`,
    limits: {
      current: userProfile.ai_usage_count,
      limit: userProfile.ai_usage_limit
    },
    features: [
      "AI-powered memoir analysis",
      "Context-aware responses",
      "Character and theme tracking",
      "Writing suggestions"
    ]
  };

  return (
    <>
      {/* Upgrade popup - shown when user tries to use chat with no credits */}
      {showUpgradePopup && (
        <UpgradePrompt
          data={upgradeData}
          onClose={() => setShowUpgradePopup(false)}
          onUpgrade={() => {
            window.location.href = '/subscription';
          }}
        />
      )}
    <div className="chat-interface">
      <div className="chat-header">
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="chat-clear-btn"
            title="Clear chat"
          >
            Clear
          </button>
        )}
      </div>

      {/* Chat Status */}
      {statsLoading ? (
        <div className="chat-status loading">
          <span className="status-icon">⏳</span>
          <span>Loading chat status...</span>
        </div>
      ) : error ? (
        <div className="chat-status error">
          <span className="status-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={loadChatStats} className="retry-btn">Retry</button>
        </div>
      ) : chatStats && !chatStats.chat_ready ? (
        <div className="chat-status warning">
          <span className="status-icon">ℹ️</span>
          <span>
            Chat not ready yet. Your memoir content is being indexed.
            {chatStats.indexed_chunks === 0 ? ' Please save some chapter content first.' :
              ` Processing ${chatStats.indexed_chunks} content chunks...`}
          </span>
        </div>
      ) : chatStats && chatStats.chat_ready && showReadyMessage ? (
        <div className={`chat-status ready ${readyMessageAnimating ? 'slide-up' : ''}`}>
          <span>Ready to Chat</span>
        </div>
      ) : null}

      {/* Chat Messages */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            <h4>Ask me anything about your book</h4>
            <p>I can help you explore themes, characters, timeline, and provide writing suggestions based on your content.</p>
            <div className="chat-suggestions">
              <button
                onClick={() => setInputMessage("Where do I mention my friend Jack?")}
                className="suggestion-btn"
              >
                Where do I mention my friend Jack?
              </button>
              <button
                onClick={() => setInputMessage("Did I already write about")}
                className="suggestion-btn"
              >
                Did I already write about..
              </button>
              <button
                onClick={() => setInputMessage("What writing improvements do you suggest?")}
                className="suggestion-btn"
              >
                Writing suggestions
              </button>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`chat-message ${message.type}`}>
              <div className="message-content">
                <div className={`message-text ${message.type === 'assistant' ? 'chat-formatted-content' : ''}`}>
                  {message.type === 'assistant' ? formatMessageText(message.content) : message.content}
                </div>
                {message.error && (
                  <div className="message-error">
                    <span className="error-icon">⚠️</span>
                    <span>Error: {message.error}</span>
                  </div>
                )}
              </div>
              <div className="message-timestamp">
                {formatTimestamp(message.timestamp)}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="chat-message assistant loading">
            <div className="message-content">
              <div className="message-text">
                <span className="thinking-icon">🤔</span>
                <span>Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input */}
      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={chatStats?.chat_ready ? "Ask me about your writings..." : "Waiting for content to be indexed..."}
            disabled={isLoading || !chatStats?.chat_ready}
            rows={1}
            className="chat-input"
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading || !chatStats?.chat_ready}
            className="chat-send-btn"
            title="Send message"
          >
            <Send size={18} className={isLoading ? 'send-loading' : 'send-icon'} />
          </button>
        </div>
      </div>
    </div>
    </>
  );
};